/**
 * Para onde ir depois de entrar, e como isso atravessa o desvio pelo Entra.
 *
 * O caminho de senha nunca precisou disto: o `?redirect=` que o `AuthGuard`
 * anexa está na URL da própria tela de login, e o `navigate` acontece na mesma
 * página. O caminho de SSO sai da aplicação — `/api/auth/entra/login`, o
 * provedor, o callback — e volta num redirect cujo destino é **configuração**
 * (`ENTRA_POST_LOGIN_REDIRECT`), por decisão do ADR de 2026-08-17 (#12). Nada da
 * requisição original sobrevive à ida e volta, então quem abria um link para
 * `/projects/<id>/runs` e entrava pela Microsoft aterrava sempre na raiz, e o
 * link compartilhado só funcionava para quem tinha senha.
 *
 * O destino viaja no `sessionStorage`, e é por isso que a propriedade do ADR
 * continua de pé: ele **não** atravessa o IdP. O callback segue redirecionando
 * para a configuração; o último salto é interno, mesma origem, e validado aqui
 * pelo mesmo `safeRedirect` das duas pontas. Nenhum destino controlado por
 * terceiro entra na conversa com o provedor.
 */

/** Destino quando não veio `?redirect=`. Ver S-073 decisão 12. */
export const DEFAULT_REDIRECT = "/projects";

const PENDING_KEY = "mygrow_post_login_redirect";

/**
 * Janela em que um destino guardado ainda vale.
 *
 * A ida ao provedor leva segundos, não minutos, e o que sobra depois disso é
 * lixo de uma tentativa abandonada — a aba que ficou aberta, o login que falhou,
 * o botão clicado por engano. Sem prazo, o valor esperaria indefinidamente e
 * sequestraria a **próxima** entrada de sessão daquela aba, que é pior que
 * perder o deep link.
 */
const MAX_AGE_MS = 5 * 60 * 1000;

/**
 * O `?redirect=` só vale se apontar para dentro da própria origem.
 *
 * O `AuthGuard` põe esse param quando barra alguém, então ele é legítimo — mas
 * também chega pela URL, e numa tela de login um destino externo é grave: a
 * vítima abre o link, vê a tela verdadeira no domínio verdadeiro, digita as
 * credenciais de verdade, e só depois de autenticar é jogada para fora.
 *
 * A checagem é **resolver e comparar a origem**, não uma lista de prefixos
 * proibidos. Foi justamente uma lista de prefixos que falhou aqui: o guard
 * anterior era `startsWith("/")`, e `//evil.com` começa com `/`. O parser de URL
 * já sabe o que a lista teria de reaprender caso a caso — que `//host` é
 * protocol-relative, que `\` vale `/` em http(s) (`/\evil.com` é o mesmo
 * ataque), que tab e quebra de linha somem antes da resolução.
 *
 * Devolve caminho relativo, nunca URL absoluta: mesmo válido, um destino
 * absoluto não tem por que atravessar o `navigate`.
 */
export function safeRedirect(raw: string | null): string {
  if (!raw) return DEFAULT_REDIRECT;
  try {
    const target = new URL(raw, window.location.origin);
    if (target.origin !== window.location.origin) return DEFAULT_REDIRECT;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    // Origem opaca ou entrada que nem parseia. Nesses casos não há como afirmar
    // que o destino é interno, e o padrão é o único destino que se sabe seguro.
    return DEFAULT_REDIRECT;
  }
}

/**
 * Guarda o destino antes de sair para o provedor.
 *
 * Já validado na escrita, para que o que fica armazenado seja sempre um caminho
 * interno — a validação na leitura é a segunda, não a única. `sessionStorage` e
 * não `localStorage`: o valor pertence a esta aba e a esta tentativa de entrar.
 *
 * Só grava destino que não é o padrão. Guardar `/projects` seria ocupar a chave
 * para dizer o que já aconteceria sem ela.
 */
export function rememberPostLoginRedirect(raw: string | null): void {
  const target = safeRedirect(raw);
  if (target === DEFAULT_REDIRECT) return;
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ target, at: Date.now() }));
  } catch {
    // Aba privada, cota cheia, storage desligado por política. Perder o deep
    // link é degradação aceitável; quebrar o login não é.
  }
}

/**
 * Lê e **apaga** o destino guardado. `null` quando não há nada a fazer.
 *
 * Uso único por construção: a remoção acontece antes de qualquer decisão de
 * navegação, então um erro no meio do caminho não deixa o valor armado para a
 * próxima montagem.
 */
export function takePostLoginRedirect(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { target, at } = JSON.parse(raw) as { target?: unknown; at?: unknown };
    if (typeof target !== "string" || typeof at !== "number") return null;
    if (Date.now() - at > MAX_AGE_MS) return null;
    const safe = safeRedirect(target);
    return safe === DEFAULT_REDIRECT ? null : safe;
  } catch {
    return null;
  }
}
